/**
 * Aspect — Surface library (procedural fallback + async PBR upgrade)
 * --------------------------------------------------------------
 * Generates the architectural surface materials the room builder uses — plaster
 * walls, wood and tile floors, a soft ceiling, painted baseboards/frames, and
 * transparent window glass. Materials are created instantly from offscreen
 * <canvas> textures (zero download), then asynchronously upgraded in place to
 * real photographic PBR sets (Poly Haven CC0, via texture_sets.js) as the maps
 * stream in. Because the upgrade swaps maps on the SAME material instance,
 * every wall/floor segment and the cutaway fade cache stay consistent.
 *
 * Discipline (engine rules): ONE material per surface type per room build. The
 * room builder reuses these across every wall/floor segment instead of cloning a
 * material per entity. Diffuse + normal maps are meter-tiled so the texture scale
 * reads the same regardless of room size.
 */

import * as pc from "playcanvas";
import {
  applyFinish,
  wallTiling,
  DEFAULT_WALL_FINISH,
  DEFAULT_FLOOR_FINISH,
  DEFAULT_WET_FLOOR_FINISH,
  DEFAULT_CEILING_FINISH,
} from "./texture_sets.js";

const TEX = 256;

function makeCanvas(size = TEX) {
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  return c;
}

function canvasTexture(app, canvas, { srgb = true } = {}) {
  const tex = new pc.Texture(app.graphicsDevice, {
    width: canvas.width,
    height: canvas.height,
    format: pc.PIXELFORMAT_R8_G8_B8_A8,
    mipmaps: true,
    addressU: pc.ADDRESS_REPEAT,
    addressV: pc.ADDRESS_REPEAT,
    ...("PIXELFORMAT_SRGBA8" in pc && srgb ? {} : {}),
  });
  tex.anisotropy = 8;
  tex.setSource(canvas);
  return tex;
}

/** Value-noise field (0..1) at a coarse grid, smoothly interpolated. */
function fbm(x, y, seedFn) {
  const lerp = (a, b, t) => a + (b - a) * (t * t * (3 - 2 * t));
  let amp = 0.5;
  let sum = 0;
  let freq = 1;
  for (let o = 0; o < 4; o++) {
    const xi = Math.floor(x * freq);
    const yi = Math.floor(y * freq);
    const xf = x * freq - xi;
    const yf = y * freq - yi;
    const v00 = seedFn(xi, yi);
    const v10 = seedFn(xi + 1, yi);
    const v01 = seedFn(xi, yi + 1);
    const v11 = seedFn(xi + 1, yi + 1);
    const top = lerp(v00, v10, xf);
    const bot = lerp(v01, v11, xf);
    sum += lerp(top, bot, yf) * amp;
    amp *= 0.5;
    freq *= 2;
  }
  return sum;
}

function hashSeed(salt) {
  return (xi, yi) => {
    const n = Math.sin((xi * 127.1 + yi * 311.7 + salt * 13.13)) * 43758.5453;
    return n - Math.floor(n);
  };
}

/** Convert a grayscale height canvas into a tangent-space normal map texture. */
function heightToNormal(app, heightCanvas, strength = 1.6) {
  const w = heightCanvas.width;
  const h = heightCanvas.height;
  const src = heightCanvas.getContext("2d").getImageData(0, 0, w, h).data;
  const out = makeCanvas(w);
  const octx = out.getContext("2d");
  const img = octx.createImageData(w, h);
  const at = (x, y) => src[((((y + h) % h) * w + ((x + w) % w)) * 4)] / 255;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (at(x - 1, y) - at(x + 1, y)) * strength;
      const dy = (at(x, y - 1) - at(x, y + 1)) * strength;
      const len = Math.hypot(dx, dy, 1) || 1;
      const i = (y * w + x) * 4;
      img.data[i] = ((dx / len) * 0.5 + 0.5) * 255;
      img.data[i + 1] = ((dy / len) * 0.5 + 0.5) * 255;
      img.data[i + 2] = (1 / len) * 0.5 * 255 + 127;
      img.data[i + 3] = 255;
    }
  }
  octx.putImageData(img, 0, 0);
  const tex = canvasTexture(app, out, { srgb: false });
  return tex;
}

// --- Texture painters --------------------------------------------------------

function paintPlaster(salt = 1) {
  const c = makeCanvas();
  const ctx = c.getContext("2d");
  const seed = hashSeed(salt);
  const img = ctx.createImageData(TEX, TEX);
  for (let y = 0; y < TEX; y++) {
    for (let x = 0; x < TEX; x++) {
      const n = fbm((x / TEX) * 6, (y / TEX) * 6, seed);
      const v = 226 + (n - 0.5) * 26;
      const i = (y * TEX + x) * 4;
      img.data[i] = v;
      img.data[i + 1] = v - 2;
      img.data[i + 2] = v - 6;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

function paintPlasterHeight(salt = 1) {
  const c = makeCanvas();
  const ctx = c.getContext("2d");
  const seed = hashSeed(salt + 7);
  const img = ctx.createImageData(TEX, TEX);
  for (let y = 0; y < TEX; y++) {
    for (let x = 0; x < TEX; x++) {
      const n = fbm((x / TEX) * 9, (y / TEX) * 9, seed);
      const v = n * 255;
      const i = (y * TEX + x) * 4;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

function paintWood() {
  const c = makeCanvas();
  const ctx = c.getContext("2d");
  const seed = hashSeed(3);
  const planks = 4;
  const pw = TEX / planks;
  const tones = [
    [168, 124, 80],
    [156, 112, 70],
    [180, 138, 92],
    [150, 108, 66],
  ];
  for (let p = 0; p < planks; p++) {
    const [r, g, b] = tones[p % tones.length];
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.fillRect(p * pw, 0, pw, TEX);
  }
  // Grain streaks.
  const img = ctx.getImageData(0, 0, TEX, TEX);
  for (let y = 0; y < TEX; y++) {
    for (let x = 0; x < TEX; x++) {
      const grain = fbm((x / TEX) * 3, (y / TEX) * 40, seed) - 0.5;
      const i = (y * TEX + x) * 4;
      const d = grain * 34;
      img.data[i] = Math.max(0, Math.min(255, img.data[i] + d));
      img.data[i + 1] = Math.max(0, Math.min(255, img.data[i + 1] + d * 0.85));
      img.data[i + 2] = Math.max(0, Math.min(255, img.data[i + 2] + d * 0.6));
    }
  }
  ctx.putImageData(img, 0, 0);
  // Plank seams (dark grooves).
  ctx.strokeStyle = "rgba(40,28,18,0.55)";
  ctx.lineWidth = 2;
  for (let p = 0; p <= planks; p++) {
    ctx.beginPath();
    ctx.moveTo(p * pw, 0);
    ctx.lineTo(p * pw, TEX);
    ctx.stroke();
  }
  return c;
}

function paintTile() {
  const c = makeCanvas();
  const ctx = c.getContext("2d");
  const tiles = 4;
  const tw = TEX / tiles;
  for (let ty = 0; ty < tiles; ty++) {
    for (let tx = 0; tx < tiles; tx++) {
      const shade = 214 + ((tx + ty) % 2) * 10;
      ctx.fillStyle = `rgb(${shade},${shade + 4},${shade + 8})`;
      ctx.fillRect(tx * tw, ty * tw, tw, tw);
    }
  }
  ctx.strokeStyle = "rgba(120,128,134,0.8)";
  ctx.lineWidth = 3;
  for (let i = 0; i <= tiles; i++) {
    ctx.beginPath();
    ctx.moveTo(i * tw, 0);
    ctx.lineTo(i * tw, TEX);
    ctx.moveTo(0, i * tw);
    ctx.lineTo(TEX, i * tw);
    ctx.stroke();
  }
  return c;
}

function tileHeight() {
  const c = makeCanvas();
  const ctx = c.getContext("2d");
  ctx.fillStyle = "rgb(220,220,220)";
  ctx.fillRect(0, 0, TEX, TEX);
  const tiles = 4;
  const tw = TEX / tiles;
  ctx.strokeStyle = "rgb(20,20,20)";
  ctx.lineWidth = 4;
  for (let i = 0; i <= tiles; i++) {
    ctx.beginPath();
    ctx.moveTo(i * tw, 0);
    ctx.lineTo(i * tw, TEX);
    ctx.moveTo(0, i * tw);
    ctx.lineTo(TEX, i * tw);
    ctx.stroke();
  }
  return c;
}

/** Lawn diffuse: patchy turf color with blade streaks (top-down). */
function paintGrass() {
  const c = makeCanvas();
  const ctx = c.getContext("2d");
  const patchSeed = hashSeed(42);
  const fineSeed = hashSeed(43);
  const soilSeed = hashSeed(44);
  const img = ctx.createImageData(TEX, TEX);
  for (let y = 0; y < TEX; y++) {
    for (let x = 0; x < TEX; x++) {
      const patch = fbm((x / TEX) * 5.5, (y / TEX) * 5.5, patchSeed);
      const fine = fbm((x / TEX) * 20, (y / TEX) * 20, fineSeed);
      const soil = fbm((x / TEX) * 2.2, (y / TEX) * 2.2, soilSeed);
      const mix = patch * 0.62 + fine * 0.38;
      const r = Math.floor(38 + mix * 42 + soil * 22);
      const g = Math.floor(88 + mix * 78 + fine * 24);
      const b = Math.floor(24 + mix * 26 + soil * 14);
      const i = (y * TEX + x) * 4;
      img.data[i] = Math.min(255, r);
      img.data[i + 1] = Math.min(255, g);
      img.data[i + 2] = Math.min(255, b);
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  const bladeSeed = hashSeed(45);
  for (let n = 0; n < TEX * 4; n++) {
    const bx = bladeSeed(n, 0) * TEX;
    const by = bladeSeed(n, 1) * TEX;
    const shade = bladeSeed(n, 2);
    const angle = -Math.PI / 2 + (bladeSeed(n, 3) - 0.5) * 0.6;
    const len = 3 + bladeSeed(n, 4) * 16;
    const light = shade > 0.62;
    ctx.strokeStyle = light
      ? `rgba(${148 + shade * 50},${196 + shade * 35},${72 + shade * 28},${0.1 + shade * 0.2})`
      : `rgba(${22 + shade * 18},${58 + shade * 28},${16 + shade * 10},${0.12 + shade * 0.18})`;
    ctx.lineWidth = light ? 1.1 : 0.65;
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(bx + Math.cos(angle) * len, by + Math.sin(angle) * len);
    ctx.stroke();
  }
  return c;
}

/** Height field for grass normals — blade ridges read as turf clumps from above. */
function paintGrassHeight() {
  const c = makeCanvas();
  const ctx = c.getContext("2d");
  const clumpSeed = hashSeed(46);
  const bladeSeed = hashSeed(47);
  const img = ctx.createImageData(TEX, TEX);
  for (let y = 0; y < TEX; y++) {
    for (let x = 0; x < TEX; x++) {
      const clump = fbm((x / TEX) * 7, (y / TEX) * 7, clumpSeed);
      const blade = fbm((x / TEX) * 28, (y / TEX) * 28, bladeSeed);
      const v = Math.floor((clump * 0.55 + blade * 0.45) * 255);
      const i = (y * TEX + x) * 4;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

function plain(opts) {
  const m = new pc.StandardMaterial();
  if (opts.diffuse) m.diffuse = opts.diffuse;
  if (opts.gloss !== undefined) m.gloss = opts.gloss;
  m.useMetalness = true;
  m.metalness = opts.metalness ?? 0;
  if (opts.emissive) m.emissive = opts.emissive;
  if (opts.emissiveIntensity !== undefined) m.emissiveIntensity = opts.emissiveIntensity;
  m.update();
  return m;
}

/**
 * Build the shared surface materials for one room build. Tiling is meter-based:
 * pass the envelope `width`/`depth` so floor textures repeat at a realistic scale.
 * `finishes` optionally overrides the default PBR finish per surface role
 * (persisted per-room choices flow in here from the room record).
 *
 * @param {pc.AppBase} app
 * @param {{ width:number, depth:number, height?:number, skyTexture?:pc.Texture,
 *           finishes?: { wall?:string, floor?:string, floorWet?:string } }} dims
 */
export function createSurfaceLibrary(app, { width = 6, depth = 6, height = 2.8, skyTexture = null, finishes = {} } = {}) {
  // --- Plaster wall ---
  const wallDiffuse = canvasTexture(app, paintPlaster(1));
  const wallNormal = heightToNormal(app, paintPlasterHeight(1), 1.0);
  const wall = new pc.StandardMaterial();
  wall.diffuse = new pc.Color(0.92, 0.91, 0.88);
  wall.diffuseMap = wallDiffuse;
  wall.normalMap = wallNormal;
  wall.bumpiness = 0.45;
  wall.gloss = 0.18;
  wall.useMetalness = true;
  wall.metalness = 0;
  wall.diffuseMapTiling = new pc.Vec2(2, 1.4);
  wall.normalMapTiling = new pc.Vec2(2, 1.4);
  wall.update();

  // --- Wood floor (meter-tiled: ~1.5 m plank repeat) ---
  const woodDiffuse = canvasTexture(app, paintWood());
  const woodNormal = heightToNormal(app, paintWood(), 0.5);
  const floorWood = new pc.StandardMaterial();
  floorWood.diffuse = new pc.Color(0.9, 0.86, 0.8);
  floorWood.diffuseMap = woodDiffuse;
  floorWood.normalMap = woodNormal;
  floorWood.bumpiness = 0.28;
  floorWood.gloss = 0.55;
  floorWood.useMetalness = true;
  floorWood.metalness = 0;
  const woodRepeat = new pc.Vec2(Math.max(1, width / 1.5), Math.max(1, depth / 1.5));
  floorWood.diffuseMapTiling = woodRepeat;
  floorWood.normalMapTiling = woodRepeat;
  floorWood.update();

  // --- Tile floor (bathrooms/kitchens: ~0.6 m repeat of a 4-tile block) ---
  const tileDiffuse = canvasTexture(app, paintTile());
  const tileNormal = heightToNormal(app, tileHeight(), 1.2);
  const floorTile = new pc.StandardMaterial();
  floorTile.diffuse = new pc.Color(0.96, 0.97, 0.98);
  floorTile.diffuseMap = tileDiffuse;
  floorTile.normalMap = tileNormal;
  floorTile.bumpiness = 0.4;
  floorTile.gloss = 0.72;
  floorTile.useMetalness = true;
  floorTile.metalness = 0;
  const tileRepeat = new pc.Vec2(Math.max(1, width / 1.2), Math.max(1, depth / 1.2));
  floorTile.diffuseMapTiling = tileRepeat;
  floorTile.normalMapTiling = tileRepeat;
  floorTile.update();

  // --- Ceiling (soft white; keep emissive tiny so wall joints don't glow) ---
  const ceiling = plain({
    diffuse: new pc.Color(0.96, 0.96, 0.98),
    gloss: 0.08,
    emissive: new pc.Color(0.4, 0.41, 0.44),
    emissiveIntensity: 0.05,
  });

  // --- Skirting: near-black so floor joints don't read as glowing white lines
  // in dark corners (white trim + fill light = hairline bleed).
  const baseboard = plain({
    diffuse: new pc.Color(0.03, 0.03, 0.035),
    gloss: 0.12,
    metalness: 0,
    emissive: new pc.Color(0, 0, 0),
    emissiveIntensity: 0,
  });
  const frame = plain({ diffuse: new pc.Color(0.98, 0.98, 0.99), gloss: 0.42, metalness: 0 });

  // --- Window/door glass: see-through, no emissive (dark rooms stay dark) ---
  const glass = new pc.StandardMaterial();
  glass.diffuse = new pc.Color(0.12, 0.13, 0.14);
  glass.emissive = new pc.Color(0, 0, 0);
  glass.emissiveIntensity = 0;
  glass.useMetalness = true;
  glass.metalness = 0;
  glass.gloss = 0.72;
  glass.opacity = 0.14;
  glass.blendType = pc.BLEND_NORMAL;
  glass.depthWrite = false;
  glass.useSkybox = false;
  glass.useLighting = true;
  glass.update();

  // Modern balcony kit — brushed stainless, tinted safety glass, stone coping.
  const railMetal = new pc.StandardMaterial();
  railMetal.diffuse = new pc.Color(0.52, 0.54, 0.57);
  railMetal.useMetalness = true;
  railMetal.metalness = 0.96;
  railMetal.gloss = 0.78;
  railMetal.update();

  const balconyGlass = new pc.StandardMaterial();
  balconyGlass.diffuse = new pc.Color(0.62, 0.72, 0.8);
  balconyGlass.emissive = new pc.Color(0, 0, 0);
  balconyGlass.useMetalness = true;
  balconyGlass.metalness = 0.08;
  balconyGlass.gloss = 0.92;
  balconyGlass.opacity = 0.28;
  balconyGlass.blendType = pc.BLEND_NORMAL;
  balconyGlass.depthWrite = false;
  balconyGlass.useSkybox = true;
  balconyGlass.useLighting = true;
  balconyGlass.update();

  const copingStone = new pc.StandardMaterial();
  copingStone.diffuse = new pc.Color(0.72, 0.71, 0.68);
  copingStone.useMetalness = true;
  copingStone.metalness = 0.04;
  copingStone.gloss = 0.22;
  copingStone.update();

  /** Pick the floor material that suits a room type (marble/tile for wet rooms). */
  const floorForType = (type) => (/(bath|wc|toilet|shower|kitchen|laundry|wet)/i.test(String(type ?? "")) ? floorTile : floorWood);

  // --- Async PBR upgrade: photographic maps stream in over the procedural ones.
  // Fire-and-forget; on failure the procedural canvases simply remain.
  const wallFinish = finishes.wall ?? DEFAULT_WALL_FINISH;
  const floorFinish = finishes.floor ?? DEFAULT_FLOOR_FINISH;
  const wetFinish = finishes.floorWet ?? DEFAULT_WET_FLOOR_FINISH;
  const ceilingFinish = finishes.ceiling ?? DEFAULT_CEILING_FINISH;
  void applyFinish(app, wall, wallFinish, { tiling: wallTiling(height) });
  void applyFinish(app, floorWood, floorFinish, { tileW: width, tileD: depth });
  void applyFinish(app, floorTile, wetFinish, { tileW: width, tileD: depth });
  void applyFinish(app, ceiling, ceilingFinish, { tileW: width, tileD: depth });

  return {
    wall,
    floorWood,
    floorTile,
    ceiling,
    baseboard,
    frame,
    glass,
    railMetal,
    balconyGlass,
    copingStone,
    floorForType,
    finishes: { wall: wallFinish, floor: floorFinish, floorWet: wetFinish },
  };
}

/**
 * Textured lawn material for the exterior ground plane. Tiling is meter-based on
 * the actual grass patch size so blades read at a realistic scale.
 *
 * @param {pc.AppBase} app
 * @param {number} sizeW grass plane width (m)
 * @param {number} sizeD grass plane depth (m)
 */
export function createGrassMaterial(app, sizeW, sizeD) {
  const grassDiffuse = canvasTexture(app, paintGrass());
  const grassNormal = heightToNormal(app, paintGrassHeight(), 2.0);
  const grass = new pc.StandardMaterial();
  grass.diffuse = new pc.Color(0.82, 0.96, 0.72);
  grass.diffuseMap = grassDiffuse;
  grass.normalMap = grassNormal;
  grass.bumpiness = 0.8;
  grass.gloss = 0.16;
  grass.useMetalness = true;
  grass.metalness = 0;
  grass.useSkybox = true;
  const repeat = new pc.Vec2(Math.max(2, sizeW / 0.32), Math.max(2, sizeD / 0.32));
  grass.diffuseMapTiling = repeat;
  grass.normalMapTiling = repeat;
  grass.update();
  void applyFinish(app, grass, "grass_leafy", { tileW: sizeW, tileD: sizeD });
  return grass;
}

export default createSurfaceLibrary;
